/**
 * App — top-level shell + lightweight hash routing (feed / kpi). The feed is the default route.
 * A real build would use a router; hash routing keeps this scaffold dependency-light.
 */
import { useCallback, useEffect, useState } from "react";
import { AppShell, type TabKey } from "@/components/AppShell";
import { AuthGate } from "@/components/AuthGate";
import { FeedScreen } from "@/screens/FeedScreen";
import { TablesScreen } from "@/screens/TablesScreen";
import { TimelineScreen } from "@/screens/TimelineScreen";
import { KpiScreen } from "@/screens/KpiScreen";
import { useTheme } from "@/hooks/useTheme";
import { config } from "@/api/config";
import { api } from "@/api/client";
import { fetchSession, type SessionState } from "@/api/session";
import type { Restaurant } from "@/types/contract";
import type { ConnectionState } from "@/hooks/useFeedChannel";

function tabFromHash(): TabKey {
  const h = window.location.hash.replace("#/", "");
  return h === "kpi" || h === "tables" || h === "shift" ? h : "feed";
}

export default function App() {
  const { dark, toggle } = useTheme();
  const [tab, setTab] = useState<TabKey>(tabFromHash);
  const [restaurantId, setRestaurantId] = useState<string>(
    config.defaultRestaurantId,
  );
  // Точки берём из API по роли (manager → своя, network → сеть), а не хардкодом.
  const [venues, setVenues] = useState<Restaurant[]>([
    { id: config.defaultRestaurantId, name: config.defaultRestaurantId },
  ]);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  // Сессия (SSO Rover / dev-bearer): до её ответа экраны не монтируем, чтобы
  // сокет-хуки стартовали уже с socket_token из /session.
  const [session, setSession] = useState<SessionState | null>(null);

  useEffect(() => {
    let cancelled = false;

    const adoptVenues = (list: Restaurant[]) => {
      if (cancelled || list.length === 0) return;
      setVenues(list);
      setRestaurantId((cur) =>
        list.some((v) => v.id === cur) ? cur : list[0].id,
      );
    };

    fetchSession().then((s) => {
      if (cancelled) return;
      setSession(s);
      if (s.status === "ok" && s.restaurants.length > 0) {
        adoptVenues(s.restaurants);
      } else if (s.status === "ok" || s.status === "error") {
        // Сессия без списка точек (dev) или бэкенд недоступен (мок-режим) —
        // фолбэк на /restaurants; не роняем UI, остаётся точка из конфига.
        api
          .getRestaurants()
          .then(adoptVenues)
          .catch(() => undefined);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goTab = useCallback((t: TabKey) => {
    window.location.hash = `#/${t}`;
    setTab(t);
  }, []);

  // Гейт SSO: нет живой сессии → вход через Rover; нет раздела Vision → отказ.
  // "error" (бэкенд недоступен) пропускаем внутрь — клиент деградирует сам.
  if (session === null) {
    return <div className="min-h-screen bg-canvas" aria-busy="true" />;
  }
  if (session.status === "unauthorized") return <AuthGate kind="unauthorized" />;
  if (session.status === "forbidden") return <AuthGate kind="forbidden" />;

  return (
    <AppShell
      active={tab}
      onTab={goTab}
      connection={connection}
      dark={dark}
      onToggleTheme={toggle}
    >
      {tab === "feed" ? (
        <FeedScreen
          restaurants={venues}
          restaurantId={restaurantId}
          onRestaurantChange={setRestaurantId}
          onConnectionChange={setConnection}
        />
      ) : tab === "tables" ? (
        <TablesScreen restaurantId={restaurantId} />
      ) : tab === "shift" ? (
        <TimelineScreen restaurantId={restaurantId} />
      ) : (
        <KpiScreen
          restaurantId={restaurantId}
          onConnectionChange={setConnection}
        />
      )}
    </AppShell>
  );
}
